import { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useTransferContext } from '../transfer-provider'
import { useNotificationContext } from '../../../context/notification/context'
import { TRANSFER_EVENT_COMPLETE, TRANSFER_EVENT_FAILED } from '../transfer-types/upload-base'
import { transferCompleteText, transferFailedText } from '../transfer-status'

export const COMPLETE_TOAST_MS = 6000

// How a transfer ended, now that the header's Transfers button shows only
// while one runs (owner 2026-10-19). Told only AWAY from the transfer's own
// page, which already shows it (the copy card; the uploader's error and
// Retry): a failure is an error toast that stays until it is closed, a
// completion a short one. And no transfer toast shows on ANY transfer's
// page -- an uploader's or a copy's -- where it could cover that page's own
// controls (owner review 2026-10-19: a failure toast sat on the uploader's
// Retry button); one that ends meanwhile waits until the person leaves.
// Mounted once inside the NotificationProvider (page/layout.js).
export const TransferOutcomeToasts = () => {
  const transferContext = useTransferContext()
  const notificationContext = useNotificationContext()
  const { pathname } = useLocation()

  const pathRef = useRef(pathname)
  pathRef.current = pathname
  const noticeRef = useRef(notificationContext)
  noticeRef.current = notificationContext

  // the status each transfer was last told for (or seen with)
  const told = useRef(new WeakMap())
  const started = useRef(false)
  // ended while the person was on another transfer's page: told on leaving
  const waiting = useRef([])

  const items = transferContext && transferContext.items
  const list = useMemo(() => Object.values(items || {}), [items])
  const listRef = useRef(list)
  listRef.current = list

  const onTransferPage = path => listRef.current.some(item => item.gotoPath && item.gotoPath === path)

  const show = notice => {
    const toast = noticeRef.current && noticeRef.current.toast
    if (!toast) return
    if (notice.failed) toast.error(notice.text)
    else toast.notify(notice.text, COMPLETE_TOAST_MS)
  }

  useEffect(
    () => {
      const tell = item => {
        if (told.current.get(item) === item.status) return
        told.current.set(item, item.status)

        let notice = null
        if (item.status === TRANSFER_EVENT_FAILED) notice = { failed: true, text: transferFailedText(item) }
        else if (item.status === TRANSFER_EVENT_COMPLETE) notice = { failed: false, text: transferCompleteText(item) }
        if (!notice) return

        const path = pathRef.current
        // its own page says so already
        if (item.gotoPath === path) return
        // another transfer's page: wait until the person leaves it
        if (onTransferPage(path)) waiting.current.push(notice)
        else show(notice)
      }

      const subscriptions = list.map(item => {
        if (!told.current.has(item)) {
          // Whatever had already ended before this mounted (a remount) is
          // not told again; a transfer that ended before its first render
          // here is.
          if (started.current) tell(item)
          else told.current.set(item, item.status)
        }
        const onEvent = () => tell(item)
        item.addEventListener(onEvent)
        return () => item.removeEventListener(onEvent)
      })
      started.current = true

      return () => subscriptions.forEach(stop => stop())
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list]
  )

  // Left the transfer pages: tell what waited.
  useEffect(
    () => {
      if (!waiting.current.length || onTransferPage(pathname)) return
      const notices = waiting.current
      waiting.current = []
      notices.forEach(show)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, list]
  )

  return null
}
